/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from 'react';

import { useMutation } from '@tanstack/react-query';
import { createCustomMessage } from 'react-chatbot-kit';

import ChatbotLoader from './ChatBotLoader';
import { getChat } from '../../axiosClient/draftApi';
import { RootState, useTypedSelector } from '../../redux/store';

const getIssues = (issues: any) => {
  let para: any = [];
  issues.map((issue: any) => {
    const contents = Object.keys(issue);
    contents.map((key) => {
      const clauseData = issue[key];
      para = [...para, ...clauseData.summary];
    });
  });
  return para;
};

const getSystemQuestion = (
  botFrom: any,
  selectedText: string,
  contractType: string
) => {
  switch (botFrom.type) {
    case 'insights': {
      const insightsPara = botFrom.content.data.map(
        (data: any) => data.normalized_text
      );
      return `You are assisting an attorney reviewing a ${contractType}. Attorney is currently focussed on ${
        botFrom.content.clause
      }. The text of the clause is given here: ${insightsPara.toString()}`;
    }
    case 'global': {
      if (selectedText) {
        return `You are assisting an attorney reviewing a ${contractType}. Attorney is currently focussed on some selected text from the contract . The text selected is ${selectedText}.`;
      } else {
        return `You are assisting an attorney reviewing a ${contractType}.`;
      }
    }
    case 'issues': {
      const clauseText = botFrom.content.data.map(
        (data: any) => data.normalized_text
      );
      const issuesPara = getIssues(botFrom.content.issues);
      return `You are assisting an attorney reviewing a ${contractType}. Attorney is currently focussed on certain issues arising out of ${
        botFrom.content.clause
      } . The text of the clause is given here:${clauseText?.toString()}. The issues identified are ${issuesPara?.toString()}`;
    }
    case 'clause_library': {
      return `You are assisting an attorney reviewing a ${contractType}. Attorney is currently focussed on reviewing a clause library for ${
        botFrom.content.clause
      } . The text of the clauses given in the Clause library are: ${botFrom.content.data.toString()}`;
    }
    default:
      return `You are assisting an attorney reviewing a ${contractType}`;
  }
};

const ActionProvider = ({
  createChatBotMessage,
  setState,
  children,
  state,
}: any) => {
  const { selectedText } = useTypedSelector((state: RootState) => state.draft);

  useEffect(() => {
    localStorage.removeItem('chat_payload');
    const messageWithProperties = createCustomMessage('New chat', 'custom', {});
    let pushMessage = true;
    if (
      state.messages[state.messages.length - 1]?.payload?.type === 'initial' ||
      state.messages[state.messages.length - 1].type === 'custom'
    ) {
      pushMessage = false;
    }

    setState((prev: any) => ({
      ...prev,
      payload: [],
      ...(pushMessage && {
        messages: [...prev.messages, messageWithProperties],
      }),
    }));
  }, [selectedText, state.botFrom]);

  const { mutate } = useMutation({
    mutationKey: ['chat-boot-response'],
    mutationFn: getChat,
    onSuccess: (response: any) => {
      const content = response.answer.content;
      const nextMessage = {
        role: 'assistant',
        content: content,
      };
      setState((prev: any) => ({
        ...prev,
        payload: [...prev.payload, nextMessage],
      }));
      localStorage.setItem(
        'chat_payload',
        JSON.stringify([...state.payload, nextMessage])
      );

      const messageWithProperties = createChatBotMessage(
        'Here is the response!',
        {
          widget: 'ultraResponse',
          payload: {
            botMessage: `${content}`,
            citations: response.answer?.context?.citations || [],
            id: crypto.randomUUID(),
          },
        }
      );
      setState((prev: any) => {
        const newPrevMsg = prev.messages.slice(0, -1);
        return {
          ...prev,
          messages: [...newPrevMsg, messageWithProperties],
        };
      });
    },
  });

  const handleMessage = (message: any) => {
    const nextMessage = {
      role: 'user',
      content: message,
    };

    mutate({
      messages: [
        {
          role: 'system',
          content: getSystemQuestion(
            state.botFrom,
            selectedText,
            state.contractType
          ),
        },
        ...state.payload,
        nextMessage,
      ],
      file_name: state.fileNames,
      response_format: 'json_object',
      type:
        state.botFrom.type === 'global'
          ? selectedText
            ? 'chat_bot'
            : 'chat_with_data'
          : state.botFrom.type,
    });
    const messageWithProperties = createChatBotMessage(<ChatbotLoader />);
    setState((prev: any) => ({
      ...prev,
      payload: [...prev.payload, nextMessage],
      messages: [...prev.messages, messageWithProperties],
    }));
    localStorage.setItem(
      'chat_payload',
      JSON.stringify([...state.payload, nextMessage])
    );
  };

  return (
    <div>
      {React.Children.map(children, (child) => {
        return React.cloneElement(child, {
          actions: {
            handleMessage,
          },
        });
      })}
    </div>
  );
};

export default ActionProvider;
